import { useRef, useState } from 'react';
import { useBrowser } from '../../common/services/BrowserService';
import { useLogger } from '../../common/services/LoggerService/LoggerService';
import { useServices } from '../../common/services/Services';

export interface PreloadSlot<T> {
  readonly name: string;
  default: T | (() => T);
}

export const usePreloadSlot = <T>(slot: PreloadSlot<T>): T => {
  const { cache } = useServices();
  const logger = useLogger();
  const [resolvedDefault] = useState(slot.default);
  const cachedValue = cache?.get(slot);
  const hasWarned = useRef(false);
  const { window } = useBrowser();

  if (!hasWarned.current) {
    hasWarned.current = true;

    if (cachedValue === undefined) {
      if (logger) {
        logger.warn(`Preload cache miss: "${slot.name}"`);
      } else if (window) {
        // eslint-disable-next-line no-console
        console.log(`Preload cache miss: "${slot.name}"`);
      }
    }
  }

  return cachedValue === undefined ? resolvedDefault : cachedValue;
};

export const useOptionalPreloadSlot = <T>(slot: PreloadSlot<T>): T => {
  const { cache } = useServices();
  const [resolvedDefault] = useState(slot.default);
  const cachedValue = cache?.get(slot);

  return cachedValue === undefined ? resolvedDefault : cachedValue;
};

export const makePreloadSlot = <T>(name: string, defaultValue: T | (() => T)): PreloadSlot<T> => ({
  name,
  default: defaultValue,
});
